/* This file is the source. There is no .css twin.
 *
 * The register is a string rather than a stylesheet import so the app controls
 * exactly where it lands in the cascade. The base bundle and the register are
 * both `:root` blocks with equal specificity, so ORDER is the only thing that
 * decides which wins, and a CSS import gives no guarantee about that. Injected
 * into a <style> appended after the base, the register always wins, including
 * over the base bundle's prefers-color-scheme block.
 *
 * It started as a .css file next to this one. That was deleted rather than
 * kept, because two copies of the same rules is the drift this repo keeps
 * finding, and a template literal is no harder to read.
 */
export const registerMerchantCss = `
/* The merchant register: the dark teal ground of
 * \`Damga V1 Counter and Wallet.dc.html\` (SHA-256 c60ff6b5…9e14).
 *
 * MOVED FROM PLUM ON 2026-09-17, Surface 3. The landing and the customer app
 * went to the teal family that day; the artboard for this surface already
 * declared these values under the same token names (--paper, --ink, --seal),
 * so the counter changes register without a component learning a new name.
 *
 * WHAT THE ACCENT MEANS HERE. In plum the accent was amber and sat within a few
 * points of --warning, so a refusal and a held stamp had to share it and be
 * told apart by a dash. In teal the accent, the warning and the danger are far
 * apart, so each panel edge now says its own thing: danger for a refusal that
 * needs attention, warning for a stamp held on the device, as the artboard
 * draws A6 and A4.
 *
 * WHY THE RADII ARE HERE TOO. The base system is a 2px-corner system by design;
 * this one is not, and redefining --radius-sm/md/lg means a component that has
 * not been migrated still lands on the new scale.
 */
@font-face {
  font-family: "Schibsted Grotesk";
  src: url("/fonts/SchibstedGrotesk-variable-latin.woff2") format("woff2");
  font-weight: 400 900;
  font-display: swap;
}
@font-face {
  font-family: "Bricolage Grotesque";
  src: url("/fonts/BricolageGrotesque-variable-latin.woff2") format("woff2");
  font-weight: 400 800;
  font-display: swap;
}
/* One face for 400 to 600, as on the other two surfaces: no faked bold. */
@font-face {
  font-family: "DM Mono";
  src: url("/fonts/DMMono-500-latin.woff2") format("woff2");
  font-weight: 400 600;
  font-display: swap;
}
:root {
  color-scheme: dark;

  /* --- faces ------------------------------------------------------------- */
  --font-sans: "Schibsted Grotesk", system-ui, -apple-system, sans-serif;
  --font-mono: "DM Mono", ui-monospace, SFMono-Regular, monospace;
  --font-display: "Bricolage Grotesque", "Schibsted Grotesk", sans-serif;

  /* --- surfaces ---------------------------------------------------------- */
  --paper: #0d2a33;
  --paper-card: #123844;
  --paper-card-raised: #17404e;
  /* Separate from --paper, which is also read where a gradient is not valid. */
  --paper-shell: linear-gradient(168deg, #0b222a, #10333e 62%, #154351);

  /* --- ink --------------------------------------------------------------- */
  --ink: #eaf6f8;
  --ink-soft: rgba(234, 246, 248, 0.84);
  --ink-faint: rgba(234, 246, 248, 0.68);

  /* --- rules ------------------------------------------------------------- */
  --border: rgba(234, 246, 248, 0.18);
  --border-soft: rgba(234, 246, 248, 0.1);

  /* --- the accent -------------------------------------------------------- */
  --seal: #5eead4;
  --seal-strong: #a7f3e5;
  --seal-text: #5eead4;
  --text-on-seal: #062028;

  /* --- status ------------------------------------------------------------ */
  --success: #7ee3c0;
  --danger: #ffab8f;
  --warning: #e8c477;
  --success-bg: rgba(126, 227, 192, 0.12);
  --danger-bg: rgba(255, 171, 143, 0.12);
  --warning-bg: rgba(232, 196, 119, 0.14);

  /* --- radii ------------------------------------------------------------- */
  --radius-input: 10px;
  --radius-chip: 12px;
  --radius-card: 16px;
  --radius-panel: 20px;
  --radius-full: 999px;
  --radius-sm: 10px;
  --radius-md: 12px;
  --radius-lg: 16px;

  /* --- the primary button: the artboard's "Next customer" ---------------- */
  --button-primary-bg: linear-gradient(160deg, #a7f3e5, #5eead4 55%, #38bdf8);
  --button-primary-fg: #062028;
  --button-primary-radius: 16px;
  --button-primary-shadow: 0 10px 22px -12px rgba(94, 234, 212, 0.7);
  --button-primary-shadow-pressed: 0 6px 14px -10px rgba(94, 234, 212, 0.55);

  /* --- read off the artboard --------------------------------------------- */
  /* The earned stamp: the accent into sky, as A1 draws it. */
  --pip-filled: linear-gradient(150deg, #5eead4, #38bdf8 70%);
  --pip-empty-border: rgba(234, 246, 248, 0.24);
  /* A recess, darker than the ground. The artboard's value is the plum
     register's near-black red (rgba(28,4,12,.32)), left behind; this is the
     same recess in the teal family. */
  --panel-inset: rgba(3, 18, 23, 0.34);
  --panel-inset-radius: 14px;

  --button-secondary-bg: rgba(234, 246, 248, 0.08);
  --button-secondary-fg: #eaf6f8;
  --button-secondary-border: rgba(234, 246, 248, 0.34);
  --button-secondary-radius: 14px;
}
`;
